import type { FormKitNode, FormKitSchemaNode } from '@formkit/core'

export function formClassStyle(cols?: number, style?: string) {
  return cols ? `${formClass(cols)} ${style}` : style || ''
}
export function formClass(cols?: number) {
  switch (cols) {
    case 1:
      return 'col-span-12 lg:col-span-1'
    case 2:
      return 'col-span-12 lg:col-span-2'
    case 3:
      return 'col-span-12 lg:col-span-3'
    case 4:
      return 'col-span-12 lg:col-span-4'
    case 5:
      return 'col-span-12 lg:col-span-5'
    case 6:
      return 'col-span-12 lg:col-span-6'
    case 7:
      return 'col-span-12 lg:col-span-7'
    case 8:
      return 'col-span-12 lg:col-span-8'
    case 9:
      return 'col-span-12 lg:col-span-9'
    case 10:
      return 'col-span-12 lg:col-span-10'
    case 11:
      return 'col-span-12 lg:col-span-11'
    case 12:
      return 'col-span-12 lg:col-span-12'
    default:
      return 'grid gap-x-2 grid-cols-1 lg:grid-cols-12'
  }
}

export function onlyNumbers(str: string): string {
  return str.replace(/\D/g, '')
}

export function decodeFields(obj: any): any {
  const decoded = {}
  for (const key in obj) {
    if (key.startsWith('dt_')) {
      decoded[key] = obj[key] ? formatDateTimeIso(obj[key]) : null
    } else if (key.startsWith('bb_')) {
      try {
        decoded[key] = Decode64(obj[key])
      } catch (e) {
        // decoded[key] = obj[key]
        console.error(e)
      }
    } else {
      decoded[key] = obj[key]
    }
  }
  return decoded
}
export function encodeFields(obj: object): object {
  const encoded = {}
  for (const key in obj) {
    if (key.startsWith('dt_')) {
      encoded[key] = obj[key] ? unformatDateTimeIso(obj[key]) : null
    } else if (key.startsWith('bb_')) {
      encoded[key] = Encode64(obj[key])
    } else if (Array.isArray(obj[key])) {
      encoded[key] = obj[key].join(',')
    } else {
      encoded[key] = obj[key]
    }
  }
  return encoded
}
// funcao que recebe um objeto e um array de campos e retorna um objeto com os campos do objeto que estão no array
export function filterObjectByFields(obj: object, fields: string[]): object {
  return fields.reduce((acc, field) => {
    if (field in obj) {
      acc[field] = obj[field]
    }
    return acc
  }, {})
}

export function copyObject(schema: any): object {
  return JSON.parse(JSON.stringify(schema))
}

export function copyObjectFromFields(fields: string, value: any): object {
  const result = {}
  if (fields.includes(',')) {
    fields.split(',')?.forEach((field: string) => {
      result[field] = value[field] || null
    })
  }
  return result
}
// funcao que converte objeto em uma string com o nome das keys separadas por virgula
type ObjectKeysToCsvOptions = {
  onlyDefined?: boolean
  sort?: boolean
}
export function keyToCsv(obj: any, options: ObjectKeysToCsvOptions = {}): string {
  if (!obj || typeof obj !== 'object')
    return ''

  const { onlyDefined = false, sort = false } = options

  const record = obj as Record<string, any>
  const keys = Object.keys(record).filter((key) => {
    if (!onlyDefined)
      return true
    const value = record[key]
    return value !== null && value !== undefined
  })

  if (sort)
    keys.sort((a, b) => a.localeCompare(b))

  return keys.join(',')
}
// funcao que recebe um schema e um objeto e retorna um objeto com os campos do schema que tem valor
export function copyObjectFromSchema(schema: FormKitSchemaNode[], values: any) {
  if (!schema || !values)
    return {}

  const result = {}
  schema.forEach((node: FormKitSchemaNode) => {
    const field = node as any
    if (field.name)
      result[field.name] = values[field.name] ?? null
  })
  return result
}
// funcao que recebe um schema e um objeto e retorna uma string com os campos do schema que tem valor
export function mergeSchemaData(schema: FormKitSchemaNode[], data: any): string {
  return schema
    .filter((item: any) => data[item.name])
    .map((item: any) => {
      if (data[item.name])
        return `${item.label}:  ${data[item.name]}`
    })
    .join('\n')
}
// funcao que recebe um schema e retorna um array com os nomes dos campos do schema
export function getFieldName(schema: FormKitSchemaNode[]): string[] {
  if (!Array.isArray(schema))
    return []
  return schema.reduce((acc: string[], node: FormKitSchemaNode) => {
    const item = node as any
    if (item.name) {
      if (!('group,step,multi-step').includes(item.$formkit)) {
        if (!item.ignore)
          acc.push(item.name)
      }
    }
    if (item.children) {
      acc.push(...getFieldName(item.children))
    }
    return acc
  }, [])
}

type InvalidField = { name: string, label?: string, errors: string[] }
// funcao que recebe um node e retorna um array com os campos inválidos
export function collectInvalidFields(node: FormKitNode): InvalidField[] {
  const invalid: InvalidField[] = []

  const visit = (n: FormKitNode) => {
    const ctx: any = n.context
    const isValid = ctx?.state?.valid
    const name = n.name

    if (name && isValid === false) {
      const label = n.props?.label as string | undefined
      const msgs: any[] = Array.isArray(ctx?.messages) ? ctx.messages : Object.values(ctx?.messages ?? {})
      const errors = (msgs ?? [])
        .filter(m => m && (m.type === 'error' || m.key === 'error') && (m.visible ?? true))
        .map(m => String(m.value ?? m.message ?? m.key ?? 'Erro'))
      invalid.push({ name, label, errors })
    }

    if (Array.isArray(n.children)) {
      n.children.forEach((c: any) => {
        if (c)
          visit(c as FormKitNode)
      })
    }
  }

  visit(node)
  return invalid
}

// funcao que recebe um schema e retorna um schema com as props popover e openOnClick true para os nodes do tipo autocomplete e dropdown
export function addPopoverProps(schema: FormKitSchemaNode[]): FormKitSchemaNode[] {
  return schema.map((node: any) => {
    if (node?.$formkit === 'autocomplete' || node?.$formkit === 'dropdown') {
      node.popover = true
      node.openOnClick = true
      node.selectionRemovable = true
      node.debounce = node.debounce || 0
    }
    return node
  })
}

export function parseJson(payload: string): any | null {
  try {
    return JSON.parse(payload)
  } catch {
    useMessage().showError('Json inválido !')
    return null
  }
}
